import {
  apiClient,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
} from './api.service';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: 'admin' | 'user';
  };
}

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
  preferences?: UserPreferences;
  lastLogin?: string;
  createdAt: string;
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'system';
  notifications?: {
    email?: boolean;
    browser?: boolean;
    riskAlerts?: boolean;
    weeklyDigest?: boolean;
  };
  dashboardLayout?: string;
  defaultSpaceId?: string;
}

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

/**
 * Login with email and password
 */
export const login = async (
  credentials: LoginCredentials
): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>(
    '/auth/login',
    credentials
  );
  setAuthToken(response.accessToken);
  return response;
};

/**
 * Register new user (development only)
 */
export const register = async (data: RegisterData): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/register', data);
  setAuthToken(response.accessToken);
  return response;
};

/**
 * Logout - clear token
 */
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/logout', {});
  } catch {
    // Ignore errors on logout
  } finally {
    removeAuthToken();
  }
};

/**
 * Get current user info
 */
export const getCurrentUser = async (): Promise<CurrentUser> => {
  return apiClient.get<CurrentUser>('/auth/me');
};

/**
 * Refresh token
 */
export const refreshToken = async (): Promise<AuthResponse> => {
  const response = await apiClient.post<AuthResponse>('/auth/refresh', {});
  setAuthToken(response.accessToken);
  return response;
};

export const authService = {
  login,
  register,
  logout,
  getCurrentUser,
  refreshToken,
  getToken: getAuthToken,
  setToken: setAuthToken,
  removeToken: removeAuthToken,
  isAuthenticated,
};
